import type { Mode, VibeTag } from "@/types";
import i18n, { type TranslationKey } from "@/i18n";

function translatedOption(key: TranslationKey, value: string) {
  return {
    get label() {
      return i18n.t(key);
    },
    value,
  };
}

function translatedVibe(key: TranslationKey, emoji: string): VibeTag {
  return {
    emoji,
    get label() {
      return i18n.t(key);
    },
  };
}

function translatedMode(
  id: Mode,
  emoji: string,
  titleKey: TranslationKey,
  subtitleKey: TranslationKey,
) {
  return {
    id,
    emoji,
    get title() {
      return i18n.t(titleKey);
    },
    get subtitle() {
      return i18n.t(subtitleKey);
    },
  };
}

export function toOptionTitleCase(value: string) {
  return value
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export const VIBES: VibeTag[] = [
  translatedVibe("options.vibes.quietEnergy", "🌿"),
  translatedVibe("options.vibes.openToChat", "👋"),
  translatedVibe("options.vibes.newHere", "🧭"),
  translatedVibe("options.vibes.studyMode", "📚"),
  translatedVibe("options.vibes.coffeeLover", "☕️"),
  translatedVibe("options.vibes.lookingAround", "✨"),
  translatedVibe("options.vibes.deepConversations", "💬"),
  translatedVibe("options.vibes.lightConversations", "🌤️"),
];

export const MODES: {
  id: Mode;
  emoji: string;
  title: string;
  subtitle: string;
}[] = [
  translatedMode("meet", "🤝", "options.modes.meet.title", "options.modes.meet.subtitle"),
  translatedMode("work", "💻", "options.modes.work.title", "options.modes.work.subtitle"),
  translatedMode("networking", "🚀", "options.modes.networking.title", "options.modes.networking.subtitle"),
  translatedMode("language", "🌍", "options.modes.language.title", "options.modes.language.subtitle"),
  translatedMode("curious", "✨", "options.modes.curious.title", "options.modes.curious.subtitle"),
];

export const FRIENDSHIP_GOALS: {
  label: string;
  value: string;
}[] = [
  translatedOption("options.friendshipGoals.makeNewFriends", "Make New Friends"),
  translatedOption("options.friendshipGoals.buildCloseFriendships", "Build Close Friendships"),
  translatedOption("options.friendshipGoals.findActivityPartners", "Find Activity Partners"),
  translatedOption("options.friendshipGoals.haveCasualChats", "Have Casual Chats"),
  translatedOption("options.friendshipGoals.joinSmallGroups", "Join Small Groups"),
  translatedOption("options.friendshipGoals.findCommunity", "Find Community"),
  translatedOption("options.friendshipGoals.buildSocialConfidence", "Build Social Confidence"),
  translatedOption("options.friendshipGoals.seeWhatHappens", "See What Happens"),
  translatedOption("options.friendshipGoals.other", "other"),
];

export const GENDER_OPTIONS: {
  label: string;
  value: string;
}[] = [
  translatedOption("options.gender.woman", "woman"),
  translatedOption("options.gender.man", "man"),
  translatedOption("options.gender.nonBinary", "non-binary"),
  translatedOption("options.gender.other", "another-identity"),
  translatedOption("options.gender.preferNotToSay", "prefer-not-to-say"),
  translatedOption("options.gender.custom", "custom"),
];

export const RELIGION_OPTIONS: {
  label: string;
  value: string;
}[] = [
  translatedOption("options.religion.christian", "christian"),
  translatedOption("options.religion.muslim", "muslim"),
  translatedOption("options.religion.jewish", "jewish"),
  translatedOption("options.religion.hindu", "hindu"),
  translatedOption("options.religion.buddhist", "buddhist"),
  translatedOption("options.religion.spiritual", "spiritual"),
  translatedOption("options.religion.agnostic", "agnostic"),
  translatedOption("options.religion.atheist", "atheist"),
  translatedOption("options.religion.other", "other"),
  translatedOption("options.religion.preferNotToSay", "prefer-not-to-say"),
];

export const POLITICS_OPTIONS: {
  label: string;
  value: string;
}[] = [
  translatedOption("options.politics.liberal", "liberal"),
  translatedOption("options.politics.moderate", "moderate"),
  translatedOption("options.politics.conservative", "conservative"),
  translatedOption("options.politics.independent", "independent"),
  translatedOption("options.politics.notPolitical", "not-political"),
  translatedOption("options.politics.preferNotToSay", "prefer-not-to-say"),
];

export const LANGUAGE_OPTIONS: {
  label: string;
  value: string;
}[] = [
  translatedOption("options.languages.english", "English"),
  translatedOption("options.languages.spanish", "Spanish"),
  translatedOption("options.languages.french", "French"),
  translatedOption("options.languages.arabic", "Arabic"),
  translatedOption("options.languages.portuguese", "Portuguese"),
  translatedOption("options.languages.german", "German"),
  translatedOption("options.languages.italian", "Italian"),
  translatedOption("options.languages.mandarin", "Mandarin"),
  translatedOption("options.languages.hindi", "Hindi"),
  translatedOption("options.languages.yoruba", "Yoruba"),
  translatedOption("options.languages.igbo", "Igbo"),
  translatedOption("options.languages.hausa", "Hausa"),
  translatedOption("options.languages.irish", "Irish"),
  translatedOption("options.languages.polish", "Polish"),
  translatedOption("options.languages.ukrainian", "Ukrainian"),
  translatedOption("options.languages.russian", "Russian"),
  translatedOption("options.languages.turkish", "Turkish"),
  translatedOption("options.languages.japanese", "Japanese"),
  translatedOption("options.languages.korean", "Korean"),
  translatedOption("options.languages.vietnamese", "Vietnamese"),
  translatedOption("options.languages.other", "Other"),
];
